import React, { useEffect, useRef } from 'react';
import { Center } from '@mantine/core';
import { useNavigate, useSearchParams } from 'react-router';
import { peekOutlookOAuthReturn } from 'src/common/utils/email/outlookOAuthReturn';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import PageLoader from 'src/UI/loader/PageLoader';
import { useMicrosoftEmailOAuthCallbackMutation } from 'src/secret-menu/microsoft-email/hooks/useMicrosoftEmailOAuthCallbackMutation';

export const MicrosoftEmailOAuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const { completeMicrosoftEmailOAuth } = useMicrosoftEmailOAuthCallbackMutation();
  const navigate = useNavigate();

  const code = searchParams.get('code') ?? '';
  const state = searchParams.get('state') ?? '';
  const error = searchParams.get('error') ?? '';
  const errorDescription = searchParams.get('error_description') ?? '';

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;

    // When the flow was started from a Siesta email-draft card, return the user to the page
    // (and conversation) they connected from; the SiestaBar consumes the stored key on mount.
    // Otherwise this is the secret-menu flow and it navigates back there as before.
    const returnPath = peekOutlookOAuthReturn()?.path ?? '/secret-menu/microsoft-email';

    if (!code && !error) {
      navigate('/secret-menu');
      return;
    }

    if (error) {
      hasRunRef.current = true;
      const detail = errorDescription
        ? `${errorDescription} (${error})`
        : `The provider returned the error: ${error}.`;
      notifyAndLogError(
        'The connection was not successful',
        `There was an error connecting to your Microsoft account. ${detail} Please try again or contact support if the issue persists.`,
        new Error(`OAuth error: ${error}`),
      );
      navigate(returnPath);
      return;
    }

    if (code) {
      hasRunRef.current = true;
      (async () => {
        try {
          await completeMicrosoftEmailOAuth({ authCode: code, state });
          navigate(returnPath);
        } catch (err) {
          notifyAndLogError('Failed to connect', 'Something went wrong. Please try again.', err);
          navigate(returnPath);
        }
      })();
    }
  }, [code, error, errorDescription, state, completeMicrosoftEmailOAuth, navigate]);

  return (
    <Center h="100%">
      <PageLoader />
    </Center>
  );
};
